// dependencies
import { List, Map } from "immutable";
import moment from "moment";

// config
import config from "./Config";

// selectors
import { getURLParam } from "../../../selectors/navigation";

export const getReviewCycleID = (state) => getURLParam(state, "cycleId");
export const getLocalState = (state) => state.page.getIn([config.pageName], Map());
export const getBulkEditingForm = (state) => getLocalState(state).get("bulkEditForm", null);
export const getBulkEditGroupState = (state, groupName) =>
	getLocalState(state).getIn(["bulkEditGroups", groupName, "isEditing"], false);
export const getBulkEditGroupInputNames = (state, groupName) =>
	getLocalState(state).getIn(["bulkEditGroups", groupName, "inputList"], List());
export const getBulkEditInputValue = (state, inputName) =>
	getLocalState(state).getIn(["bulkEditInputs", inputName], null);

/** Gets the lastStartDate of the selected form bulk update */
export const getFormLastStartDate = (state, notSetValue = null) =>
	getLocalState(state).getIn(["bulkEditFormProps", "lastStartDate"], notSetValue);

export const getSelectedFormName = (state, notSetValue = null) =>
	getLocalState(state).getIn(["bulkEditForm"], notSetValue);

/**
 * Collects all the bulk update data for sending to the server.
 */
export const getBulkUpdatesRequestBody = (state) => {
	const bulkEditGroups = getLocalState(state).get("bulkEditGroups", Map());

	const updates = [];

	// creates the update list
	bulkEditGroups.keySeq().forEach((key) => {
		if (bulkEditGroups.getIn([key, "isEditing"], false)) {
			const inputs = bulkEditGroups.getIn([key, "inputList"], List());
			inputs.forEach((name) => {
				let value = getBulkEditInputValue(state, name);
				const { type } = config.bulkUpdateInputAttributes[name];

				if (type === "date" && moment.isMoment(value)) {
					value = value.toISOString();
				}

				updates.push({ field: name, value });
			});
		}
	});

	return updates;
};

/** Get the selected template id */
export const getReviewTemplateID = (state, notSetValue = null) => {
	const local = getLocalState(state);
	return local.getIn(["inputs", "template", "id"], notSetValue);
};

export const getCopyCycleID = (state) => state.navigation.getIn(["page", "query", "copyCycleId"], null);

/** Get the start date time input value */
export const getStartDateTime = (state, notSetValue = null) => {
	const local = getLocalState(state);
	return local.getIn(["inputs", "startdate"], notSetValue);
};

/** Get the list of review template forms. */
export const getReviewTemplateForms = (state) => getLocalState(state).get("templateForms", List());

/** Get a list of form indexes the user is currently editing. */
export const getEditingForms = (state) => getLocalState(state).get("editingForms", List());

/**
 * Collect the data from the state and create the payload to send to the server
 */
export const getCyclePayload = (state, formType) => {
	const local = getLocalState(state);
	const filteredForms = getReviewTemplateForms(state).filter((form) => form.get("type") === formType);

	const formattedForms = filteredForms.map((form) => {
		const updates = form.get("assignment", Map());

		// convert moments to iso strings
		const mutatedUpdates = updates.withMutations((mutable) => {
			if (mutable.has("authorsDueDate")) {
				const authorsDueDate = mutable.get("authorsDueDate");
				mutable.set("authorsDueDate", authorsDueDate ? authorsDueDate.toISOString() : null);
			}

			if (mutable.has("signersDueDate")) {
				const signersDueDate = mutable.get("signersDueDate");
				mutable.set("signersDueDate", signersDueDate ? signersDueDate.toISOString() : null);
			}

			if (mutable.has("authors")) {
				mutable.set(
					"authors",
					mutable.get("authors", List()).map((employee) => employee.get("id"))
				);
			}

			if (mutable.has("signers")) {
				mutable.set(
					"signers",
					mutable.get("signers", List()).map((employee) => employee.get("id"))
				);
			}
		});

		const formObject = {
			assignment: mutatedUpdates,
			type: form.getIn(["type"], null),
			formID: form.getIn(["form", "id"], null),
		};

		return formObject;
	});

	const payload = {
		assignments: formattedForms.toJS(),
		cycleName: local.getIn(["inputs", "cyclename"], ""),
		template: local.getIn(["inputs", "template", "id"], null),
		startDate: local.getIn(["inputs", "startdate"], null),
		adminEdit: local.getIn(["inputs", "adminedit"], null),
		group: local.getIn(["inputs", "grouppicker", "id"], null),
	};

	if (moment.isMoment(payload.startDate)) {
		payload.startDate = payload.startDate.toISOString();
	}

	return payload;
};
